<template>
  <ExecuteLockGuard>
    <FloatingMenu
      v-tooltip="study.titleAndAbstractScreening"
      placement="bottom"
      :arrow="false"
      :offset="[0, 4]"
    >
      <template #activator="{ show }">
        <StudyActionButton
          v-tooltip.top="
            !study.titleAndAbstractScreening && 'Screening based on title and/or abstract'
          "
          :is-loading="isLoading"
          :state="
            study.isDuplicate
              ? ButtonState.disabled
              : study.titleAndAbstractScreening === ''
                ? ButtonState.inactive
                : study.titleAndAbstractScreening === 'Included'
                  ? ButtonState.included
                  : ButtonState.excluded
          "
          :is-review-read-only="review.isReviewReadonly.value"
          @click.stop="show"
        >
          Title / Abstract
        </StudyActionButton>
      </template>
      <template #content>
        <div class="flex flex-col p-1 w-72 max-h-100 overflow-y-auto" @click.stop.prevent>
          <!-- Included -->
          <button
            class="flex items-center gap-2 px-2.5 py-2 rounded-md transition-colors"
            :class="isIncluded ? 'bg-green-50 text-green-700' : 'text-gray-600 hover:bg-green-50'"
            @click.stop="toggleIncluded"
          >
            <i-material-symbols-check-circle-outline
              class="size-4 shrink-0"
              :class="isIncluded ? 'text-green-600' : 'text-gray-400'"
            />
            <span class="text-sm" :class="isIncluded ? 'font-semibold' : 'font-normal'"
              >Included</span
            >
            <span v-if="isIncluded" class="ml-auto size-1.5 rounded-full bg-green-600 shrink-0" />
          </button>

          <div
            v-if="review.entity.value?.plan?.screeningPlan?.titleAndAbstractCriteria?.length"
            class="h-px bg-gray-100 my-1"
          />

          <template
            v-for="criterion in review.entity.value?.plan?.screeningPlan?.titleAndAbstractCriteria"
            :key="criterion"
          >
            <button
              class="flex items-center gap-2 px-2.5 py-2 rounded-md transition-colors"
              :class="
                study.titleAndAbstractScreening === criterion
                  ? 'bg-red-50 text-red-700'
                  : 'text-gray-600 hover:bg-red-50'
              "
              @click.stop="toggleExcluded(criterion)"
            >
              <i-material-symbols-cancel-outline
                class="size-4 shrink-0"
                :class="
                  study.titleAndAbstractScreening === criterion ? 'text-red-600' : 'text-gray-400'
                "
              />
              <span
                class="text-sm truncate"
                :class="
                  study.titleAndAbstractScreening === criterion ? 'font-semibold' : 'font-normal'
                "
                >{{ criterion }}</span
              >
              <span
                v-if="study.titleAndAbstractScreening === criterion"
                class="ml-auto size-1.5 rounded-full bg-red-600 shrink-0"
              />
            </button>
          </template>
        </div>
      </template>
    </FloatingMenu>
  </ExecuteLockGuard>
</template>
<script lang="ts" setup>
import FloatingMenu from "@app/components/Global/FloatingMenu.vue";
import { ButtonState } from "@app/types";
import { ReviewKey } from "@app/views/Review/use-review";
import { ReviewItem } from "@core/domain/models/reviewItem.model";
import { INCLUDED } from "@core/domain/types/included-screening-criterion.type";
import StudyActionButton from "../../StudyActionButton.vue";
import ExecuteLockGuard from "../../ExecuteLockGuard.vue";
import { injectStrict } from "@app/utils/injectStrict";
import { computed } from "vue";

const review = injectStrict(ReviewKey);
const props = defineProps<{
  study: ReviewItem;
  isLoading?: boolean;
}>();

const isIncluded = computed(() => props.study.titleAndAbstractScreening === INCLUDED);

const emit = defineEmits<{
  (e: "clearTitleAndAbstractScreening"): void;
  (e: "setTitleAndAbstractScreening", value: string): void;
}>();

function toggleIncluded() {
  if (props.study.titleAndAbstractScreening === INCLUDED) emit("clearTitleAndAbstractScreening");
  else if (
    !!props.study.pdfFile ||
    (review.entity.value?.plan?.screeningPlan.titleAndAbstractCriteria?.length ?? 0) > 0
  )
    emit("setTitleAndAbstractScreening", INCLUDED);
}

function toggleExcluded(criterion: string) {
  if (props.study.titleAndAbstractScreening === criterion) emit("clearTitleAndAbstractScreening");
  else emit("setTitleAndAbstractScreening", criterion);
}
</script>
